import { useEffect, useState } from 'react';
import { api, receiptUrl, type LineItem, type Receipt } from '../lib/api';
import { ErrorNote } from './ui';

/**
 * The receipts held by a report, with a way to remove one.
 *
 * Distinct from "Remove" inside the expense editor, which only DETACHES a
 * receipt from that line and leaves it on the report to attach elsewhere.
 * This deletes it outright -- the wrong photo, the accidental double upload,
 * the blurry one -- which matters because every ready receipt is reproduced
 * in the PDF packet finance receives.
 */
export function ReceiptList({
  receipts,
  lineItems,
  canEdit,
  onChanged,
}: {
  receipts: Receipt[];
  lineItems: LineItem[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Keyed on the set of ready receipts rather than the array identity, so a
  // reload that returns the same receipts does not refetch every signed URL.
  // `thumbs` is read through the setter callback rather than closed over,
  // which is what keeps it out of the dependencies without suppressing a rule.
  const readyIds = receipts.filter((r) => r.status === 'ready').map((r) => r.id).join(',');

  useEffect(() => {
    let cancelled = false;
    for (const id of readyIds ? readyIds.split(',') : []) {
      receiptUrl(id, 'thumb')
        .then((url) => {
          if (cancelled || !url) return;
          setThumbs((current) => (current[id] ? current : { ...current, [id]: url }));
        })
        .catch(() => undefined); // a PDF or an undecodable image simply has none
    }
    return () => { cancelled = true; };
  }, [readyIds]);

  async function remove(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api.delete(`/receipts/${id}`);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that receipt');
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  if (receipts.length === 0) {
    return <p className="mt-3 text-sm text-slate-500">No receipts on this report yet.</p>;
  }

  return (
    <div className="mt-3 space-y-2">
      <ErrorNote>{error}</ErrorNote>

      <ul className="space-y-2">
        {receipts.map((receipt) => {
          // Naming the lines makes the consequence visible before the tap,
          // rather than leaving someone to discover the gap at submit time.
          const attached = lineItems.filter((l) => l.receipt_id === receipt.id);
          const isConfirming = confirming === receipt.id;

          return (
            <li key={receipt.id} className="rounded-lg border border-slate-200 p-2">
              <div className="flex items-center gap-3">
                {thumbs[receipt.id] ? (
                  <img src={thumbs[receipt.id]} alt="" className="h-12 w-12 rounded object-cover" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400">
                    {receipt.contentType === 'application/pdf' ? 'PDF' : 'img'}
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{receipt.originalFilename}</p>
                  <p className="text-xs text-slate-500">
                    {receipt.status === 'ready'
                      ? attached.length === 0
                        ? 'not attached to any expense'
                        : `on ${attached.length} expense${attached.length === 1 ? '' : 's'}`
                      : receipt.status}
                  </p>
                </div>

                {canEdit && !isConfirming ? (
                  <button
                    type="button"
                    className="min-h-11 px-2 text-sm text-red-600 underline"
                    onClick={() => setConfirming(receipt.id)}
                  >
                    Delete
                  </button>
                ) : null}
              </div>

              {isConfirming ? (
                <div className="mt-2 rounded-lg bg-red-50 p-2">
                  <p className="text-sm text-red-800">
                    {attached.length > 0
                      ? `Delete this receipt? ${attached.length === 1 ? 'The expense using it' : `The ${attached.length} expenses using it`} will need another before the report can be submitted.`
                      : 'Delete this receipt? It is not attached to an expense.'}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <button type="button" className="btn-secondary flex-1" disabled={busy === receipt.id}
                            onClick={() => setConfirming(null)}>
                      Keep it
                    </button>
                    <button type="button" className="btn-danger flex-1" disabled={busy === receipt.id}
                            onClick={() => void remove(receipt.id)}>
                      {busy === receipt.id ? 'Deleting…' : 'Yes, delete'}
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
